/**
 * Controlled activity editor for scheduling, priority, duration, bundle, P/RJ profile, custom
 * fields, and dependencies. task/open seed the form; onSave receives a partial UI activity and the
 * parent owns activity persistence. Loads workspace/attribute choices and manages attribute
 * definitions, with optional delete/reschedule and session actions.
 *
 * @module apps/web/src/components/blocks/TaskEditor
 */

import { Fragment, useState, useEffect, useEffectEvent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  TaskUI,
  AttributeDef,
  TaskStateProfile,
  deriveTaskStateMode,
  taskToUI,
  type PrerequisiteInput,
} from '@fluxability/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, CalendarClock, Trash2, Settings, Loader2, Play, Timer } from 'lucide-react';
import { api } from '@/lib/api';
import { formatMissingPlanningFields, getMissingPlanningFields } from '@/lib/activityReadiness';
import { parseAttributeOptions } from '@/lib/attributeOptions';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { ActivityScheduleFields } from '@/components/activities/ActivityScheduleFields';
import { ActivitySessionLogger } from '@/components/activities/ActivitySessionLogger';
import { groupBundlesByProject } from '@/lib/bundleGroups';
import { SplitPolicyField } from '@/components/activities/SplitPolicyField';
import { PrerequisitePicker } from '@/components/activities/PrerequisitePicker';
import { useBundlePurposeGate } from '@/components/workspace/BundlePurposeDialog';

/** Saved editor fields; a new activity also carries the prerequisites to link or create. */
export type TaskEditorSave = Partial<TaskUI> & {
  /** Prerequisites for a new activity: existing IDs and new step titles. Absent when editing. */
  prerequisites?: PrerequisiteInput[];
};

/** Existing-or-new activity draft boundary and parent-owned persistence, deletion, and rescheduling actions. */
interface TaskEditorProps {
  /** Existing activity to edit, or null for creation. */
  task: TaskUI | null;
  /** Controlled dialog visibility. */
  open: boolean;
  /** Request that the parent close the dialog. */
  onClose: () => void;
  /** Publish partial UI fields; the parent owns saving the activity. */
  onSave: (task: TaskEditorSave) => void | Promise<void>;

  /** Optional parent-owned deletion action. */
  onDelete?: () => void;
  /** Optional action asking the server to place this activity tomorrow. */
  onRescheduleTomorrow?: () => void;
}

/** Unsaved custom-field definition values collected by the activity editor. */
interface AttributeDraft {
  /** Unsaved label for a new personal field. */
  name: string;
  /** Selected value kind for the draft field. */
  data_type: 'float' | 'enum';
  /** Draft activity-specificity score used by field weighting. */
  specificity: number;
  /** Draft relative influence of the field's signal. */
  field_weight: number;
  /** Optional transformation expression entered for numeric mapping. */
  custom_math_map: string;
  /** Draft enum choices before submission normalization. */
  options: string;
  /** Draft lower numeric bound before validation. */
  min_val: string;
  /** Draft upper numeric bound before validation. */
  max_val: string;
  /** Draft feature position associated with the field definition. */
  tensor_index: number;
  /** Whether the draft field should require a value. */
  required: boolean;
}

/** Reserved custom-attribute key retaining activity ordering metadata through editor writes. */
const ACTIVITY_ORDER_ATTR = 'depends_on_activity_ids';

/**
 * Controlled activity editor for scheduling, priority, duration, bundle, P/RJ profile, custom
 * fields, and dependencies. task/open seed the form; onSave receives a partial UI activity and the
 * parent owns activity persistence. Loads workspace/attribute choices and manages attribute
 * definitions, with optional delete/reschedule and session actions.
 */
export function TaskEditor({
  task,
  open,
  onClose,
  onSave,
  onDelete,
  onRescheduleTomorrow,
}: TaskEditorProps) {
  const { projects, bundles, loadWorkspace, getBundle, getProject } = useWorkspaceStore();
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const allTasks = useTasksStore(s => s.tasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const upsertTasks = useTasksStore(s => s.upsertTasks);
  const purposeGate = useBundlePurposeGate();
  const activeBundles = useMemo(
    () => bundles.filter(bundle => bundle.status === 'active'),
    [bundles]
  );
  const bundleGroups = useMemo(
    () =>
      groupBundlesByProject(
        activeBundles,
        projects.filter(project => project.status === 'active')
      ),
    [activeBundles, projects]
  );
  const [formData, setFormData] = useState<Partial<TaskUI>>({
    title: task?.title || '',
    description: task?.description || '',
    bundleId: task?.bundleId || null,
    priority: task?.priority || 3,
    estimatedDuration: task?.estimatedDuration || 60,
    deadline: task?.deadline || undefined,
    stateProfile: task?.stateProfile || {
      mode: 'neutral',
      required_productivity_energy: null,
      required_rejuvenation_energy: null,
      productivity_return: null,
      rejuvenation_return: null,
      resistance: null,
    },
    customAttributes: task?.customAttributes || {},
    scheduledStart: task?.scheduledStart || null,
    scheduledEnd: task?.scheduledEnd || null,
    splitPolicy: task?.splitPolicy ?? 'auto',
  });

  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [isManagingFields, setIsManagingFields] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<'float' | 'enum'>('float');
  const [newFieldSpecificity, setNewFieldSpecificity] = useState(5.0);
  const [newFieldWeight, setNewFieldWeight] = useState(1.0);
  const [newFieldMathMap, setNewFieldMathMap] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldMin, setNewFieldMin] = useState('');
  const [newFieldMax, setNewFieldMax] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(null);
  const [editingAttributeDraft, setEditingAttributeDraft] = useState<AttributeDraft | null>(null);
  const [savingAttributeId, setSavingAttributeId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingField, setIsCreatingField] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoggingSession, setIsLoggingSession] = useState(false);
  const [pendingSteps, setPendingSteps] = useState<string[]>([]);
  const [stepBusy, setStepBusy] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchAttributes();
      loadWorkspace().catch(error => console.error('Failed to load workspace', error));
    }
  }, [open, loadWorkspace]);

  // Workspace reloads update activeBundles; they should not erase edits in progress, so the form
  // resets from the latest values only when the dialog opens or another activity is edited.
  const resetForm = useEffectEvent(() => {
    setFormError(null);
    setFormData({
      title: task?.title || '',
      description: task?.description || '',
      bundleId: task?.bundleId || (activeBundles.length === 1 ? activeBundles[0]!.id : null),
      priority: task?.priority || 3,
      estimatedDuration: task?.estimatedDuration || 30,
      deadline: task?.deadline,
      stateProfile: task?.stateProfile || {
        mode: 'neutral',
        required_productivity_energy: null,
        required_rejuvenation_energy: null,
        productivity_return: null,
        rejuvenation_return: null,
        resistance: null,
      },
      customAttributes: task?.customAttributes || {},
      scheduledStart: task?.scheduledStart || null,
      scheduledEnd: task?.scheduledEnd || null,
      splitPolicy: task?.splitPolicy ?? 'auto',
    });
    setPendingSteps([]);
    setStepError(null);
  });
  const taskId = task?.id;
  useEffect(() => {
    if (open) resetForm();
  }, [taskId, open]);

  const fetchAttributes = async () => {
    try {
      const response = await api.user.getAttributes();
      if (response.success && response.data) {
        setAvailableAttributes(response.data);
      }
    } catch (e) {
      console.error('Failed to fetch attributes', e);
    }
  };

  const missingRequiredFields = useMemo(
    () => getMissingPlanningFields(formData, availableAttributes),
    [formData, availableAttributes]
  );
  const missingRequiredLabel = formatMissingPlanningFields(missingRequiredFields);

  useEffect(() => {
    if (missingRequiredFields.length === 0 && formError?.startsWith('Fill ')) {
      setFormError(null);
    }
  }, [missingRequiredFields.length, formError]);

  // Logging past work needs no scheduled slot; the server accepts unscheduled activities.
  const canLogActivitySession = Boolean(task);
  const canEditScheduledTime = Boolean(
    formData.customAttributes?.fixed_start ||
    hasUsableDate(formData.scheduledStart) ||
    hasUsableDate(formData.scheduledEnd)
  );
  const prerequisiteIds = useMemo(
    () => normalizeDependencyIds(formData.customAttributes?.[ACTIVITY_ORDER_ATTR]),
    [formData.customAttributes]
  );
  const prerequisiteOptions = useMemo(
    () =>
      allTasks
        .filter(candidate => candidate.id !== task?.id && candidate.status !== 'cancelled')
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [allTasks, task?.id]
  );
  const prerequisiteTasks = useMemo(
    () =>
      prerequisiteIds
        .map(id => allTasks.find(candidate => candidate.id === id))
        .filter(Boolean) as TaskUI[],
    [allTasks, prerequisiteIds]
  );

  const handleSave = async () => {
    if (missingRequiredFields.length > 0) {
      setFormError(`Fill ${missingRequiredLabel} before this activity can be scheduled.`);
      return;
    }
    if (
      hasUsableDate(formData.scheduledStart) &&
      hasUsableDate(formData.scheduledEnd) &&
      new Date(formData.scheduledEnd!).getTime() <= new Date(formData.scheduledStart!).getTime()
    ) {
      setFormError('Scheduled end must be after scheduled start.');
      return;
    }
    // Imported bundles must be given a purpose before they receive a new activity.
    const selectedBundle = getBundle(formData.bundleId);
    if ((!task || task.bundleId !== formData.bundleId) && selectedBundle?.needsPurpose) {
      purposeGate.requirePurpose(selectedBundle.id, handleSave);
      return;
    }
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        ...(!task && (prerequisiteIds.length > 0 || pendingSteps.length > 0)
          ? {
              prerequisites: [
                ...prerequisiteIds.map(id => ({ activity_id: id })),
                ...pendingSteps.map(title => ({ title })),
              ],
            }
          : {}),
        customAttributes: {
          ...(formData.customAttributes || {}),
          needs_review: false,
          required_fields_complete: true,
          missing_required_fields: [],
        },
      });
      onClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not save this activity.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttributeChange = (attrId: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...(prev.customAttributes || {}),
        [attrId]: value,
      },
    }));
  };

  const handleDurationChange = (value: string) => {
    const duration = parseInt(value, 10) || 0;
    setFormData(prev => ({
      ...prev,
      estimatedDuration: duration,
      scheduledEnd:
        duration > 0 && hasUsableDate(prev.scheduledStart)
          ? addMinutesIso(prev.scheduledStart!, duration)
          : prev.scheduledEnd,
    }));
  };

  const handleScheduledStartChange = (value: string) => {
    const nextStart = dateTimeLocalToIso(value);
    setFormData(prev => ({
      ...prev,
      scheduledStart: nextStart,
      scheduledEnd:
        nextStart && Number(prev.estimatedDuration || 0) > 0
          ? addMinutesIso(nextStart, Number(prev.estimatedDuration))
          : nextStart
            ? prev.scheduledEnd
            : null,
    }));
  };

  const handleScheduledEndChange = (value: string) => {
    const nextEnd = dateTimeLocalToIso(value);
    setFormData(prev => {
      const startMs = prev.scheduledStart ? new Date(prev.scheduledStart).getTime() : NaN;
      const endMs = nextEnd ? new Date(nextEnd).getTime() : NaN;
      const nextDuration =
        Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
          ? Math.max(1, Math.round((endMs - startMs) / 60000))
          : prev.estimatedDuration;
      return {
        ...prev,
        scheduledEnd: nextEnd,
        estimatedDuration: nextDuration,
      };
    });
  };

  const setPrerequisiteIds = (ids: string[]) => {
    setFormData(prev => ({
      ...prev,
      customAttributes: {
        ...(prev.customAttributes || {}),
        [ACTIVITY_ORDER_ATTR]: Array.from(new Set(ids.filter(Boolean))),
      },
    }));
  };

  const addPrerequisite = (id: string) => {
    if (!id) return;
    setPrerequisiteIds([...prerequisiteIds, id]);
  };

  // A new activity creates its steps on save; an existing one creates the step now and links it.
  const addPrerequisiteStep = async (title: string) => {
    if (!task) {
      setPendingSteps(prev => [...prev, title]);
      return;
    }
    setStepBusy(true);
    setStepError(null);
    try {
      const response = await api.tasks.update(task.id, { add_prerequisites: [{ title }] });
      const saved = response.data?.task;
      if (!response.success || !saved) {
        setStepError(response.error?.message || 'The step was not added.');
        return;
      }
      const steps = response.data?.created_prerequisites || [];
      upsertTasks([...steps, saved].map(taskToUI));
      // Keep unsaved picks in the form; only the new step's link comes from the server.
      setPrerequisiteIds([...prerequisiteIds, ...steps.map(step => step.id)]);
    } catch (error) {
      setStepError(error instanceof Error ? error.message : 'The step was not added.');
    } finally {
      setStepBusy(false);
    }
  };

  const handleStateProfileChange = (key: StateScoreKey, value: string) => {
    setFormData(prev => ({
      ...prev,
      stateProfile: deriveProfileUpdate(prev.stateProfile, key, value),
    }));
  };

  const createField = async () => {
    if (!newFieldName.trim()) return;
    setIsCreatingField(true);
    try {
      const response = await api.user.addAttribute({
        name: newFieldName,
        data_type: newFieldType,
        specificity: newFieldSpecificity,
        field_weight: newFieldWeight,
        custom_math_map: newFieldMathMap || null,
        options: newFieldOptions,
        min_val: newFieldMin === '' ? null : Number(newFieldMin),
        max_val: newFieldMax === '' ? null : Number(newFieldMax),
        required: newFieldRequired,
      });
      if (response.success) {
        setNewFieldName('');
        setNewFieldMathMap('');
        setNewFieldOptions('');
        setNewFieldSpecificity(5.0);
        setNewFieldWeight(1.0);
        setNewFieldMin('');
        setNewFieldMax('');
        setNewFieldRequired(false);
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsCreatingField(false);
    }
  };

  const deleteField = async (id: string) => {
    try {
      const response = await api.user.deleteAttribute(id);
      if (response.success) {
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const startEditingField = (attr: AttributeDef) => {
    setEditingAttributeId(attr.attr_id);
    setEditingAttributeDraft({
      name: attr.name,
      data_type: attr.data_type === 'enum' ? 'enum' : 'float',
      specificity: Number(attr.specificity ?? 5),
      field_weight: Number(attr.field_weight ?? 1),
      custom_math_map: attr.custom_math_map || '',
      options: parseAttributeOptions(attr).join(', '),
      min_val: attr.min_val == null ? '' : String(attr.min_val),
      max_val: attr.max_val == null ? '' : String(attr.max_val),
      tensor_index: Number(attr.tensor_index || 0),
      required: Boolean(attr.required),
    });
  };

  const saveEditingField = async () => {
    if (!editingAttributeId || !editingAttributeDraft?.name.trim()) return;
    setSavingAttributeId(editingAttributeId);
    try {
      const response = await api.user.updateAttribute(editingAttributeId, {
        name: editingAttributeDraft.name,
        data_type: editingAttributeDraft.data_type === 'enum' ? 'enum' : 'float',
        specificity: editingAttributeDraft.specificity,
        field_weight: editingAttributeDraft.field_weight,
        custom_math_map: editingAttributeDraft.custom_math_map || null,
        options: editingAttributeDraft.options,
        min_val:
          editingAttributeDraft.min_val === '' ? null : Number(editingAttributeDraft.min_val),
        max_val:
          editingAttributeDraft.max_val === '' ? null : Number(editingAttributeDraft.max_val),
        tensor_index: editingAttributeDraft.tensor_index,
        required: editingAttributeDraft.required,
      });
      if (response.success) {
        setEditingAttributeId(null);
        setEditingAttributeDraft(null);
        fetchAttributes();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAttributeId(null);
    }
  };

  const handleAttributeDefinitionUpdated = (updatedAttr: AttributeDef) => {
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none !translate-y-[-50%] flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[760px] sm:rounded-lg sm:border sm:p-6">
          <DialogHeader className="bg-background/96 shrink-0 border-b px-4 pb-3 pr-12 pt-[calc(1rem+env(safe-area-inset-top))] text-left backdrop-blur sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
            <div className="min-w-0">
              <DialogTitle>{task ? 'Edit Activity' : 'New Activity'}</DialogTitle>
              <DialogDescription>
                {task ? 'Make changes to your activity' : 'Create a new activity'}
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsManagingFields(!isManagingFields)}
              className="w-full sm:w-auto"
            >
              <Settings className="mr-2 h-4 w-4" />
              {isManagingFields ? 'Done' : 'Manage Fields'}
            </Button>
          </DialogHeader>

          <div className="native-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
            {isManagingFields ? (
              <div className="my-2 grid gap-4 rounded-md border-b border-t bg-slate-50 p-4 py-4 dark:bg-slate-900">
                <h3 className="text-sm font-medium">Manage Custom Fields</h3>

                <div className="space-y-4">
                  {availableAttributes.map(attr => {
                    const isEditing = editingAttributeId === attr.attr_id && editingAttributeDraft;
                    return (
                      <div
                        key={attr.attr_id}
                        className="rounded border bg-white p-3 shadow-sm dark:bg-slate-800"
                      >
                        {isEditing ? (
                          <div className="grid gap-3">
                            <div className="grid gap-3 sm:grid-cols-[1fr_120px_120px]">
                              <div>
                                <Label className="text-xs">Field Name</Label>
                                <Input
                                  value={editingAttributeDraft.name}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current ? { ...current, name: event.target.value } : current
                                    )
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Specificity</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.1"
                                  value={editingAttributeDraft.specificity}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current
                                        ? { ...current, specificity: Number(event.target.value) }
                                        : current
                                    )
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Weight</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.1"
                                  value={editingAttributeDraft.field_weight}
                                  onChange={event =>
                                    setEditingAttributeDraft(current =>
                                      current
                                        ? { ...current, field_weight: Number(event.target.value) }
                                        : current
                                    )
                                  }
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-[150px_1fr]">
                              <div>
                                <Label className="text-xs">Type</Label>
                                <Select
                                  value={editingAttributeDraft.data_type}
                                  onValueChange={value =>
                                    setEditingAttributeDraft(current =>
                                      // The select offers only the two draft types.
                                      current
                                        ? {
                                            ...current,
                                            data_type: value as AttributeDraft['data_type'],
                                          }
                                        : current
                                    )
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="float">Numeric</SelectItem>
                                    <SelectItem value="enum">Dropdown</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {editingAttributeDraft.data_type === 'enum' ? (
                                <div>
                                  <Label className="text-xs">Dropdown Options</Label>
                                  <Input
                                    value={editingAttributeDraft.options}
                                    onChange={event =>
                                      setEditingAttributeDraft(current =>
                                        current
                                          ? { ...current, options: event.target.value }
                                          : current
                                      )
                                    }
                                    placeholder="Focused, Calm, Resistant"
                                    className="h-8 text-sm"
                                  />
                                </div>
                              ) : (
                                <div className="grid gap-3 sm:grid-cols-3">
                                  <div>
                                    <Label className="text-xs">Min</Label>
                                    <Input
                                      type="number"
                                      value={editingAttributeDraft.min_val}
                                      onChange={event =>
                                        setEditingAttributeDraft(current =>
                                          current
                                            ? { ...current, min_val: event.target.value }
                                            : current
                                        )
                                      }
                                      placeholder="Auto"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Max</Label>
                                    <Input
                                      type="number"
                                      value={editingAttributeDraft.max_val}
                                      onChange={event =>
                                        setEditingAttributeDraft(current =>
                                          current
                                            ? { ...current, max_val: event.target.value }
                                            : current
                                        )
                                      }
                                      placeholder="Auto"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Math Map</Label>
                                    <Input
                                      value={editingAttributeDraft.custom_math_map}
                                      onChange={event =>
                                        setEditingAttributeDraft(current =>
                                          current
                                            ? { ...current, custom_math_map: event.target.value }
                                            : current
                                        )
                                      }
                                      placeholder="linear_pull"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>

                            <label className="bg-muted/20 flex min-h-9 items-center gap-2 rounded-md border px-3 text-xs">
                              <Checkbox
                                checked={editingAttributeDraft.required}
                                onCheckedChange={value =>
                                  setEditingAttributeDraft(current =>
                                    current ? { ...current, required: Boolean(value) } : current
                                  )
                                }
                              />
                              Required for scheduling
                            </label>

                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingAttributeId(null);
                                  setEditingAttributeDraft(null);
                                }}
                                disabled={savingAttributeId === attr.attr_id}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveEditingField}
                                disabled={savingAttributeId === attr.attr_id}
                              >
                                {savingAttributeId === attr.attr_id && (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Save Field
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-medium">{attr.name}</div>
                              <div className="text-xs text-slate-500">
                                {attr.data_type} | Specificity: {attr.specificity} | Weight:{' '}
                                {attr.field_weight ?? 1}
                                {attr.required ? ' | Required' : ' | Optional'}
                                {attr.min_val != null && attr.max_val != null
                                  ? ` | Range: ${attr.min_val}-${attr.max_val}`
                                  : ' | Auto range'}
                                {attr.options
                                  ? ` | ${parseAttributeOptions(attr).length} options`
                                  : ''}
                                {attr.custom_math_map ? ` | Map: ${attr.custom_math_map}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => startEditingField(attr)}
                                disabled={isCreatingField}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteField(attr.attr_id)}
                                disabled={isCreatingField}
                                className="text-red-500 hover:bg-red-50 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 grid gap-3 border-t pt-4">
                  <h4 className="text-xs font-semibold uppercase text-slate-500">Add New Field</h4>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Field Name</Label>
                      <Input
                        value={newFieldName}
                        onChange={e => setNewFieldName(e.target.value)}
                        placeholder="e.g. Energy, Emotion..."
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Specificity</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10.0"
                        value={newFieldSpecificity}
                        onChange={e => setNewFieldSpecificity(parseFloat(e.target.value) || 1.0)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Weight</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10.0"
                        value={newFieldWeight}
                        onChange={e => setNewFieldWeight(parseFloat(e.target.value) || 1.0)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={newFieldType}
                          onValueChange={v => setNewFieldType(v as AttributeDraft['data_type'])}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="float">Numeric</SelectItem>
                            <SelectItem value="enum">Dropdown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="bg-muted/20 flex h-8 items-center gap-2 rounded-md border px-3 text-xs">
                        <Checkbox
                          checked={newFieldRequired}
                          onCheckedChange={value => setNewFieldRequired(Boolean(value))}
                        />
                        Required for scheduling
                      </label>
                    </div>
                  </div>

                  {newFieldType === 'enum' && (
                    <div>
                      <Label className="text-xs">Dropdown Options</Label>
                      <Input
                        value={newFieldOptions}
                        onChange={e => setNewFieldOptions(e.target.value)}
                        placeholder="Focused, Restorative, Admin"
                        className="h-8 text-sm"
                      />
                    </div>
                  )}

                  {newFieldType === 'float' && (
                    <div className="grid gap-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="text-xs">Min (optional)</Label>
                          <Input
                            type="number"
                            value={newFieldMin}
                            onChange={e => setNewFieldMin(e.target.value)}
                            placeholder="Auto"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Max (optional)</Label>
                          <Input
                            type="number"
                            value={newFieldMax}
                            onChange={e => setNewFieldMax(e.target.value)}
                            placeholder="Auto"
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Math Map (optional)</Label>
                        <Input
                          value={newFieldMathMap}
                          onChange={e => setNewFieldMathMap(e.target.value)}
                          placeholder="linear_pull or exponential_decay"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={createField}
                    className="mt-2 w-full"
                    size="sm"
                    disabled={isCreatingField}
                  >
                    {isCreatingField && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Add Field
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 py-4">
                {(formError || missingRequiredFields.length > 0) && (
                  <div className="rounded-md border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <p className="font-medium">
                          {formData.customAttributes?.source === 'canvas'
                            ? 'Canvas assignment needs planning details.'
                            : 'Activity needs planning details.'}
                        </p>
                        <p className="mt-1 text-xs">
                          {formError || `Required before Solve: ${missingRequiredLabel}.`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Core Fields */}
                <div className="grid gap-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Activity title"
                  />
                </div>

                <div className="bg-muted/20 grid gap-4 rounded-md border p-3 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label>Bundle</Label>
                    {activeBundles.length > 0 ? (
                      <Select
                        value={formData.bundleId || ''}
                        onValueChange={value =>
                          purposeGate.requirePurpose(value, () =>
                            setFormData(prev => ({ ...prev, bundleId: value }))
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a bundle" />
                        </SelectTrigger>
                        <SelectContent>
                          {bundleGroups.map((group, groupIndex) => (
                            <Fragment key={group.id}>
                              <SelectGroup>
                                <SelectLabel className="text-muted-foreground pl-2 text-xs uppercase tracking-wide">
                                  {group.label}
                                </SelectLabel>
                                {group.bundles.map(bundle => (
                                  <SelectItem key={bundle.id} value={bundle.id}>
                                    {bundle.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                              {groupIndex < bundleGroups.length - 1 && <SelectSeparator />}
                            </Fragment>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="bg-background rounded-md border p-3">
                        <p className="text-muted-foreground text-sm">
                          Create a bundle before saving activities.
                        </p>
                        <Button asChild variant="outline" size="sm" className="mt-2">
                          <Link to="/workspace" onClick={onClose}>
                            Create bundle
                          </Link>
                        </Button>
                      </div>
                    )}
                    {getBundle(formData.bundleId)?.needsPurpose && (
                      <button
                        type="button"
                        className="text-left text-xs text-amber-700 underline-offset-2 hover:underline dark:text-amber-400"
                        onClick={() =>
                          purposeGate.requirePurpose(formData.bundleId, () => undefined)
                        }
                      >
                        This imported bundle needs a purpose before it can take new activities. Add
                        it now.
                      </button>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label>Project</Label>
                    <div className="bg-background text-muted-foreground flex h-10 items-center rounded-md border px-3 text-sm">
                      {getProject(getBundle(formData.bundleId)?.projectId)?.name || 'No project'}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="duration">Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.estimatedDuration || ''}
                      onChange={e => handleDurationChange(e.target.value)}
                      min={5}
                      step={5}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="deadline">Intended Deadline</Label>
                    <Input
                      id="deadline"
                      type="datetime-local"
                      value={
                        formData.deadline
                          ? new Date(
                              new Date(formData.deadline).getTime() -
                                new Date(formData.deadline).getTimezoneOffset() * 60000
                            )
                              .toISOString()
                              .slice(0, 16)
                          : ''
                      }
                      onChange={e => {
                        if (!e.target.value) {
                          setFormData({ ...formData, deadline: undefined });
                          return;
                        }
                        const d = new Date(e.target.value);
                        setFormData({ ...formData, deadline: d.toISOString() });
                      }}
                    />
                  </div>
                </div>

                <ActivityScheduleFields
                  attributes={formData.customAttributes || {}}
                  onChange={handleAttributeChange}
                />
                {task?.customAttributes?.split_parent_activity_id ? (
                  <p className="text-muted-foreground text-sm">
                    Part {String(task.customAttributes.split_part_index ?? '?')} of{' '}
                    {String(task.customAttributes.split_part_count ?? '?')} of “
                    {String(task.customAttributes.split_parent_title ?? 'the original activity')}”.
                    Change splitting on the original activity.
                  </p>
                ) : formData.customAttributes?.routine || formData.customAttributes?.routine_id ? (
                  <p className="text-muted-foreground text-sm">
                    Each routine occurrence stays whole.
                  </p>
                ) : (
                  <SplitPolicyField
                    value={formData.splitPolicy}
                    onChange={splitPolicy => setFormData(prev => ({ ...prev, splitPolicy }))}
                    totalMinutes={formData.estimatedDuration || 0}
                    remainingMinutes={
                      task && formData.estimatedDuration === task.estimatedDuration
                        ? (task.remainingDuration ?? undefined)
                        : undefined
                    }
                    disabled={isSaving}
                  />
                )}

                {canEditScheduledTime && (
                  <div className="bg-muted/20 grid gap-4 rounded-md border p-3 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="scheduled-start">Scheduled Start</Label>
                      <Input
                        id="scheduled-start"
                        type="datetime-local"
                        value={dateTimeLocalValue(formData.scheduledStart)}
                        onChange={event => handleScheduledStartChange(event.target.value)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="scheduled-end">Scheduled End</Label>
                      <Input
                        id="scheduled-end"
                        type="datetime-local"
                        value={dateTimeLocalValue(formData.scheduledEnd)}
                        onChange={event => handleScheduledEndChange(event.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="grid gap-4 border-t pt-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Actionable State
                    </h4>
                    <p className="text-muted-foreground text-xs">
                      P/RJ investment and return used by Solve.
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label>Mode</Label>
                      <div className="bg-muted/40 flex h-10 items-center rounded-md border px-3 text-sm font-medium">
                        {formatMode(deriveTaskStateMode(formData.stateProfile))}
                      </div>
                    </div>
                    <StateScoreField
                      label="Resistance"
                      value={formData.stateProfile?.resistance}
                      onChange={value => handleStateProfileChange('resistance', value)}
                    />
                    <StateScoreField
                      label="Required P"
                      value={formData.stateProfile?.required_productivity_energy}
                      onChange={value =>
                        handleStateProfileChange('required_productivity_energy', value)
                      }
                    />
                    <StateScoreField
                      label="Required RJ"
                      value={formData.stateProfile?.required_rejuvenation_energy}
                      onChange={value =>
                        handleStateProfileChange('required_rejuvenation_energy', value)
                      }
                    />
                    <StateScoreField
                      label="P Return"
                      value={formData.stateProfile?.productivity_return}
                      onChange={value => handleStateProfileChange('productivity_return', value)}
                    />
                    <StateScoreField
                      label="RJ Return"
                      value={formData.stateProfile?.rejuvenation_return}
                      onChange={value => handleStateProfileChange('rejuvenation_return', value)}
                    />
                  </div>
                </div>

                {/* Custom Attributes Fields */}
                {availableAttributes.length > 0 && (
                  <div className="mt-4 grid gap-4 border-t pt-4">
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      Custom Attributes
                    </h4>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {availableAttributes.map(attr => (
                        <div key={attr.attr_id} className="grid gap-2">
                          <Label htmlFor={`attr-${attr.attr_id}`}>{attr.name}</Label>
                          {attr.data_type === 'enum' ? (
                            <EnumAttributeInput
                              attr={attr}
                              value={formData.customAttributes?.[attr.attr_id] ?? ''}
                              onChange={value => handleAttributeChange(attr.attr_id, value)}
                              onAttributeUpdated={handleAttributeDefinitionUpdated}
                              triggerId={`attr-${attr.attr_id}`}
                            />
                          ) : ['Number', 'float', 'integer'].includes(attr.data_type) ? (
                            <Input
                              id={`attr-${attr.attr_id}`}
                              type="number"
                              value={formData.customAttributes?.[attr.attr_id] ?? ''}
                              onChange={e =>
                                handleAttributeChange(
                                  attr.attr_id,
                                  e.target.value === '' ? '' : parseFloat(e.target.value)
                                )
                              }
                              placeholder="Numeric value..."
                            />
                          ) : (
                            <Input
                              id={`attr-${attr.attr_id}`}
                              type="text"
                              value={formData.customAttributes?.[attr.attr_id] ?? ''}
                              onChange={e => handleAttributeChange(attr.attr_id, e.target.value)}
                              placeholder="String value..."
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <PrerequisitePicker
                  options={prerequisiteOptions}
                  selected={prerequisiteTasks}
                  pendingSteps={pendingSteps}
                  scheduledStart={formData.scheduledStart}
                  busy={stepBusy}
                  error={stepError}
                  onAdd={addPrerequisite}
                  onRemove={id =>
                    setPrerequisiteIds(prerequisiteIds.filter(existing => existing !== id))
                  }
                  onAddStep={addPrerequisiteStep}
                  onRemoveStep={index =>
                    setPendingSteps(prev => prev.filter((_, at) => at !== index))
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter className="bg-background/96 shrink-0 gap-3 border-t px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:mt-6 sm:flex sm:items-center sm:justify-between sm:border-0 sm:bg-transparent sm:p-0">
            <div className="flex flex-wrap gap-2">
              {task && onDelete && !isManagingFields && (
                <Button variant="destructive" onClick={onDelete} disabled={isSaving}>
                  Delete Activity
                </Button>
              )}
              {task && onRescheduleTomorrow && !isManagingFields && (
                <Button variant="outline" onClick={onRescheduleTomorrow} disabled={isSaving}>
                  <CalendarClock className="mr-2 h-4 w-4" />
                  Tomorrow
                </Button>
              )}
              {task && !isManagingFields && (
                <Button
                  type="button"
                  onClick={() => {
                    if (missingRequiredFields.length > 0) {
                      setFormError(`Fill ${missingRequiredLabel} before starting this activity.`);
                      return;
                    }
                    startImmersionMode(task.id);
                    onClose();
                  }}
                  disabled={isSaving || missingRequiredFields.length > 0}
                  title={
                    missingRequiredFields.length > 0
                      ? `Missing ${missingRequiredLabel}`
                      : 'Start activity'
                  }
                  className="bg-cyan-300 text-slate-950 hover:bg-cyan-200"
                >
                  <Play className="mr-2 h-4 w-4 fill-current" />
                  Start Activity
                </Button>
              )}
              {task && canLogActivitySession && !isManagingFields && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (missingRequiredFields.length > 0) {
                      setFormError(
                        `Fill ${missingRequiredLabel} before logging a session for this activity.`
                      );
                      return;
                    }
                    setIsLoggingSession(true);
                  }}
                  disabled={isSaving || missingRequiredFields.length > 0}
                  title={
                    missingRequiredFields.length > 0
                      ? `Missing ${missingRequiredLabel}`
                      : 'Log activity session'
                  }
                >
                  <Timer className="mr-2 h-4 w-4" />
                  Log Activity Session
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              {!isManagingFields && (
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Activity
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {purposeGate.dialog}
      <ActivitySessionLogger
        task={task}
        open={isLoggingSession && canLogActivitySession}
        onClose={() => setIsLoggingSession(false)}
        onLogged={updatedTask => {
          updateTask(updatedTask.id, updatedTask);
          setIsLoggingSession(false);
          onClose();
        }}
      />
    </>
  );
}

/** Check that a nonempty date value parses to a finite timestamp before deriving editor defaults. */
function hasUsableDate(value?: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  return Number.isFinite(date.getTime());
}

/** Convert a saved instant to the browser-local datetime input representation; missing or invalid timestamps display blank. */
function dateTimeLocalValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** Interpret a datetime input in the browser timezone and return an ISO instant; empty or invalid input becomes null. */
function dateTimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

/** Add a finite minute duration to a parseable instant; invalid start/duration returns null. */
function addMinutesIso(value: string, minutes: number): string | null {
  const start = new Date(value).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(minutes)) return null;
  return new Date(start + minutes * 60000).toISOString();
}

/** Read dependency IDs from arrays, JSON array strings, or legacy comma-separated strings. Keep nonempty string IDs; this parser does not validate graph cycles or ownership. */
function normalizeDependencyIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeDependencyIds(parsed);
    } catch {
      return value
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
    }
  }
  return [];
}

/** Edit a planning score while preserving an empty value as unspecified. The surrounding activity editor owns draft validation and persistence. */
function StateScoreField({
  label,
  value,
  onChange,
}: {
  /** Visible caption describing this control or metric. */
  label: string;
  /** Optional numeric score; an absent value leaves the field empty. */
  value?: number | null;
  /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        aria-label={label}
        type="number"
        min={0}
        max={10}
        step={0.5}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
        placeholder="0-10"
      />
    </div>
  );
}

/** Merge a score draft into the existing state profile, preserving blank as null, and recompute its productive/rejuvenating mode. Numeric scores are clamped before the derived mode is selected. */
function deriveProfileUpdate(
  profile: TaskStateProfile | undefined,
  key: StateScoreKey,
  value: string
): TaskStateProfile {
  const nextProfile: TaskStateProfile = {
    ...(profile || {}),
    [key]: value === '' ? null : Math.max(0, Math.min(10, Number(value))),
  };
  return {
    ...nextProfile,
    mode: deriveTaskStateMode(nextProfile),
  };
}

/** Editable numeric state-profile keys; excludes the derived mode field. */
type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

/** Map the derived state mode to a display label, falling back to Neutral for the remaining case. */
function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}
